import React, { useState, useEffect } from "react";
import { adminGet, adminPost, uploadImage } from "../common/api/api";

const TABS = [
  { id: "hero",     label: "Hero Slides",  icon: "\uD83C\uDFAC" },
  { id: "banners",  label: "Ad Banners",   icon: "\uD83D\uDCE2" },
  { id: "features", label: "Feature Bar",  icon: "\u2728" },
  { id: "brands",   label: "Brands",       icon: "\uD83C\uDFF7\uFE0F" },
  { id: "ticker",   label: "Ticker",       icon: "\uD83D\uDCE1" },
];

const ACCENT_PRESETS = ["#00d4ff","#7c3aed","#f472b6","#22c55e","#f59e0b","#ec4899","#06b6d4","#f97316","#ef4444","#a855f7"];

const BLANK_SLIDE  = { image:"", category:"", tag:"", title:"", subtitle:"", btnLabel:"Shop Now", btnLink:"/", accent:"#00d4ff" };
const BLANK_BANNER = { image:"", title:"", subtitle:"", ctaText:"Shop Now", ctaLink:"/", accent:"#00d4ff", active:true };

const inputCls = "w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] placeholder-[#4a5568] focus:outline-none focus:border-[#00d4ff]/60 text-sm";

const AccentPicker = ({ value, onChange }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <input type="color" value={value} onChange={e => onChange(e.target.value)}
      className="w-8 h-8 rounded cursor-pointer border-none bg-transparent p-0 shrink-0" />
    {ACCENT_PRESETS.map(c => (
      <button key={c} onClick={() => onChange(c)} title={c}
        style={{ background: c, width: 22, height: 22, borderRadius: 5, border: value === c ? "2px solid white" : "2px solid transparent", cursor: "pointer" }} />
    ))}
  </div>
);

const ImageSourceField = ({ value, onChange, onUpload, uploading, hint }) => (
  <div className="sm:col-span-2 space-y-3">
    <div>
      <label className="text-xs text-[#4a5568] mb-1 block">Image URL</label>
      <input className={inputCls} placeholder="https://..." value={value} onChange={e => onChange(e.target.value)} />
    </div>
    <div className="rounded-xl border border-dashed border-white/[0.12] bg-white/[0.02] p-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm text-[#e2e8f0] font-medium">Or upload image</p>
          <p className="text-[11px] text-[#4a5568] mt-1">{hint}</p>
        </div>
        <label className="px-3 py-2 rounded-lg border border-[rgba(0,212,255,0.3)] text-[#00d4ff] text-sm cursor-pointer hover:bg-[rgba(0,212,255,0.08)] transition-colors">
          {uploading ? "Uploading..." : "Choose File"}
          <input
            type="file"
            accept="image/*,.avif"
            hidden
            disabled={uploading}
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) onUpload(file);
              e.target.value = "";
            }}
          />
        </label>
      </div>
    </div>
  </div>
);

const SlideForm = ({ data, onChange, onUpload, uploading }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    <ImageSourceField
      value={data.image}
      onChange={value => onChange("image", value)}
      onUpload={onUpload}
      uploading={uploading}
      hint="Upload JPG, PNG, WebP, or AI-generated images. Uploaded file will fill the URL automatically."
    />
    <div>
      <label className="text-xs text-[#4a5568] mb-1 block">Category</label>
      <input className={inputCls} placeholder="e.g. Laptops & PCs" value={data.category} onChange={e => onChange("category", e.target.value)} />
    </div>
    <div>
      <label className="text-xs text-[#4a5568] mb-1 block">Tag / Badge</label>
      <input className={inputCls} placeholder="e.g. \uD83D\uDD25 Up to 40% OFF" value={data.tag} onChange={e => onChange("tag", e.target.value)} />
    </div>
    <div>
      <label className="text-xs text-[#4a5568] mb-1 block">Title</label>
      <input className={inputCls} placeholder="e.g. Premium Laptops" value={data.title} onChange={e => onChange("title", e.target.value)} />
    </div>
    <div>
      <label className="text-xs text-[#4a5568] mb-1 block">Button Label</label>
      <input className={inputCls} placeholder="Shop Now" value={data.btnLabel} onChange={e => onChange("btnLabel", e.target.value)} />
    </div>
    <div className="sm:col-span-2">
      <label className="text-xs text-[#4a5568] mb-1 block">Subtitle</label>
      <input className={inputCls} placeholder="Short description..." value={data.subtitle} onChange={e => onChange("subtitle", e.target.value)} />
    </div>
    <div>
      <label className="text-xs text-[#4a5568] mb-1 block">Button Link</label>
      <input className={inputCls} placeholder="/laptops" value={data.btnLink} onChange={e => onChange("btnLink", e.target.value)} />
    </div>
    <div>
      <label className="text-xs text-[#4a5568] mb-1.5 block">Accent Color</label>
      <AccentPicker value={data.accent || "#00d4ff"} onChange={v => onChange("accent", v)} />
    </div>
  </div>
);

const BannerForm = ({ data, onChange, onUpload, uploading }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    <ImageSourceField
      value={data.image}
      onChange={value => onChange("image", value)}
      onUpload={onUpload}
      uploading={uploading}
      hint="Upload banner artwork from your device, including AI-generated creatives, or paste a hosted URL."
    />
    <div>
      <label className="text-xs text-[#4a5568] mb-1 block">Title</label>
      <input className={inputCls} value={data.title} onChange={e => onChange("title", e.target.value)} />
    </div>
    <div>
      <label className="text-xs text-[#4a5568] mb-1 block">CTA Button Text</label>
      <input className={inputCls} value={data.ctaText} onChange={e => onChange("ctaText", e.target.value)} />
    </div>
    <div className="sm:col-span-2">
      <label className="text-xs text-[#4a5568] mb-1 block">Subtitle</label>
      <input className={inputCls} value={data.subtitle} onChange={e => onChange("subtitle", e.target.value)} />
    </div>
    <div>
      <label className="text-xs text-[#4a5568] mb-1 block">CTA Link</label>
      <input className={inputCls} value={data.ctaLink} onChange={e => onChange("ctaLink", e.target.value)} />
    </div>
    <div>
      <label className="text-xs text-[#4a5568] mb-1.5 block">Accent Color</label>
      <AccentPicker value={data.accent || "#00d4ff"} onChange={v => onChange("accent", v)} />
    </div>
  </div>
);

const SaveBtn = ({ saving, saved, section, onClick, label = "Save" }) => (
  <div className="flex items-center gap-3 mt-4">
    <button onClick={onClick} disabled={saving === section}
      className="px-5 py-2 bg-[rgba(0,212,255,0.15)] text-[#00d4ff] border border-[rgba(0,212,255,0.3)] rounded-lg text-sm hover:bg-[rgba(0,212,255,0.25)] cursor-pointer transition-colors disabled:opacity-50">
      {saving === section ? "Saving..." : label}
    </button>
    {saved === section && <span className="text-green-400 text-xs font-semibold">\u2713 Saved!</span>}
  </div>
);

const AdminHomePage = () => {
  const [tab, setTab] = useState("hero");
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(null);
  const [assetUploading, setAssetUploading] = useState(null);

  // Edit states
  const [editSlide, setEditSlide]   = useState(null); // { index, data }
  const [editBanner, setEditBanner] = useState(null);
  const [newBrand,   setNewBrand]   = useState({ name: "", icon: "" });
  const [newTicker,  setNewTicker]  = useState("");

  const loadContent = async () => {
    setLoading(true);
    try {
      const data = await adminGet("/homecontent");
      if (data && (data.heroSlides !== undefined || data.tickers !== undefined)) {
        setContent(data);
      } else {
        setContent(null);
      }
    } catch (err) {
      setContent(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadContent(); }, []);

  const handleImageUpload = async (target, file) => {
    setAssetUploading(target);
    try {
      const formData = new FormData();
      formData.append("product", file);
      const resp = await uploadImage(formData);
      if (!resp?.success || !resp.image_url) {
        alert("Image upload failed");
        return;
      }
      if (target === "slide") {
        setEditSlide((prev) => prev ? { ...prev, data: { ...prev.data, image: resp.image_url } } : prev);
      }
      if (target === "banner") {
        setEditBanner((prev) => prev ? { ...prev, data: { ...prev.data, image: resp.image_url } } : prev);
      }
    } catch (err) {
      alert(err.message || "Image upload failed");
    } finally {
      setAssetUploading(null);
    }
  };

  const saveSection = async (section, data) => {
    setSaving(section);
    await adminPost("/updatehomecontent", { section, data });
    setSaving(null);
    setSaved(section);
    setTimeout(() => setSaved(null), 2500);
    loadContent();
  };

  if (loading) return <div className="p-6 text-[#4a5568] text-sm">Loading home content...</div>;
  if (!content) return (
    <div className="p-6 max-[640px]:p-4">
      <p className="text-red-400 text-sm mb-3">Failed to load home content. Make sure the backend is running.</p>
      <button onClick={loadContent} className="px-4 py-2 bg-[rgba(0,212,255,0.12)] text-[#00d4ff] border border-[rgba(0,212,255,0.3)] rounded-lg text-sm cursor-pointer hover:bg-[rgba(0,212,255,0.2)]">
        Retry
      </button>
    </div>
  );

  const { heroSlides = [], adBanners = [], featureBar = [], brands = [], tickers = [] } = content;

  // ── Hero helpers ──────────────────────────────────────────────
  const saveSlide = () => {
    if (!editSlide) return;
    const arr = [...heroSlides];
    if (editSlide.index === -1) arr.push(editSlide.data);
    else arr[editSlide.index] = editSlide.data;
    saveSection("heroSlides", arr);
    setEditSlide(null);
  };
  const deleteSlide = i => saveSection("heroSlides", heroSlides.filter((_, idx) => idx !== i));

  // ── Banner helpers ──────────────────────────────────────────────
  const saveBanner = () => {
    if (!editBanner) return;
    const arr = [...adBanners];
    if (editBanner.index === -1) arr.push(editBanner.data);
    else arr[editBanner.index] = editBanner.data;
    saveSection("adBanners", arr);
    setEditBanner(null);
  };
  const deleteBanner = i => saveSection("adBanners", adBanners.filter((_, idx) => idx !== i));
  const toggleBanner = i => saveSection("adBanners", adBanners.map((b, idx) => idx === i ? { ...b, active: !b.active } : b));

  // ── Feature helpers ──────────────────────────────────────────────
  const updateFeature = (i, field, val) =>
    setContent({ ...content, featureBar: featureBar.map((f, idx) => idx === i ? { ...f, [field]: val } : f) });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white max-[640px]:text-xl">Home Page Manager</h1>
        <p className="text-sm text-[#4a5568] mt-0.5">Control all home page sections from admin panel</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap border-b border-white/[0.06] pb-4 overflow-x-auto max-[640px]:pb-3 max-[640px]:-mx-1 max-[640px]:px-1">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all cursor-pointer whitespace-nowrap max-[640px]:px-3 max-[640px]:py-1.5 ${
              tab === t.id
                ? "bg-[rgba(0,212,255,0.12)] text-[#00d4ff] border-[rgba(0,212,255,0.4)]"
                : "text-[#4a5568] border-white/[0.08] hover:border-white/[0.2] hover:text-[#a0aec0]"
            }`}>
            <span>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      {/* ═══════════════════ HERO SLIDES ═══════════════════ */}
      {tab === "hero" && (
        <div>
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <p className="text-[#a0aec0] text-sm">{heroSlides.length} slides &mdash; drag order matters</p>
            <button onClick={() => setEditSlide({ index: -1, data: { ...BLANK_SLIDE } })}
              className="px-4 py-2 bg-gradient-to-r from-[#00d4ff] to-[#0099cc] text-[#060c18] font-bold rounded-lg text-sm hover:opacity-90 cursor-pointer">
              + Add Slide
            </button>
          </div>

          <div className="space-y-2 mb-5">
            {heroSlides.map((sl, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-white/[0.03] border border-white/[0.07] rounded-xl max-[640px]:items-start max-[640px]:flex-col">
                <div className="w-2 h-10 rounded-full shrink-0" style={{ background: sl.accent || "#333" }} />
                {sl.image
                  ? <img src={sl.image} alt="" className="w-14 h-10 rounded-lg object-cover shrink-0 border border-white/[0.08]" />
                  : <div className="w-14 h-10 rounded-lg bg-white/[0.04] border border-white/[0.06] shrink-0 flex items-center justify-center text-[#2a3547] text-[10px]">No img</div>
                }
                <div className="flex-1 min-w-0 max-[640px]:w-full">
                  <p className="text-white font-semibold text-sm truncate">{sl.title || "(no title)"}</p>
                  <p className="text-[#4a5568] text-xs truncate">{sl.category} &bull; {sl.tag}</p>
                </div>
                <div className="flex gap-2 shrink-0 max-[640px]:w-full max-[640px]:justify-end">
                  <button onClick={() => setEditSlide({ index: i, data: { ...sl } })}
                    className="text-xs px-3 py-1 rounded-lg border border-[rgba(0,212,255,0.3)] text-[#00d4ff] hover:bg-[rgba(0,212,255,0.1)] cursor-pointer transition-all">Edit</button>
                  <button onClick={() => deleteSlide(i)}
                    className="text-xs px-3 py-1 rounded-lg border border-red-500/20 text-red-400 hover:bg-red-500/10 cursor-pointer transition-all">Delete</button>
                </div>
              </div>
            ))}
          </div>

          {editSlide && (
            <div className="p-5 bg-white/[0.03] border border-[rgba(0,212,255,0.2)] rounded-2xl">
              <h3 className="text-white font-bold mb-4">{editSlide.index === -1 ? "Add New Slide" : `Edit Slide ${editSlide.index + 1}`}</h3>
              <SlideForm
                data={editSlide.data}
                onChange={(field, val) => setEditSlide({ ...editSlide, data: { ...editSlide.data, [field]: val } })}
                onUpload={(file) => handleImageUpload("slide", file)}
                uploading={assetUploading === "slide"}
              />
              <div className="flex gap-2 mt-4">
                <button onClick={saveSlide} disabled={saving === "heroSlides"}
                  className="px-4 py-2 bg-[rgba(0,212,255,0.15)] text-[#00d4ff] border border-[rgba(0,212,255,0.3)] rounded-lg text-sm hover:bg-[rgba(0,212,255,0.25)] cursor-pointer disabled:opacity-50">
                  {saving === "heroSlides" ? "Saving..." : "Save Slide"}
                </button>
                <button onClick={() => setEditSlide(null)}
                  className="px-4 py-2 text-[#4a5568] border border-white/[0.08] rounded-lg text-sm hover:border-white/[0.2] cursor-pointer">Cancel</button>
              </div>
              {saved === "heroSlides" && <p className="text-green-400 text-xs mt-2">\u2713 Saved!</p>}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════ AD BANNERS ═══════════════════ */}
      {tab === "banners" && (
        <div>
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <p className="text-[#a0aec0] text-sm">{adBanners.length} banners &mdash; position = order in list</p>
            <button onClick={() => setEditBanner({ index: -1, data: { ...BLANK_BANNER } })}
              className="px-4 py-2 bg-gradient-to-r from-[#00d4ff] to-[#0099cc] text-[#060c18] font-bold rounded-lg text-sm hover:opacity-90 cursor-pointer">
              + Add Banner
            </button>
          </div>

          <div className="space-y-2 mb-5">
            {adBanners.map((b, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-white/[0.03] border border-white/[0.07] rounded-xl max-[640px]:items-start max-[640px]:flex-col">
                <span className="text-[#4a5568] text-xs font-mono w-5 shrink-0">#{i + 1}</span>
                {b.image
                  ? <img src={b.image} alt="" className="w-16 h-10 rounded-lg object-cover shrink-0 border border-white/[0.08]" />
                  : <div className="w-16 h-10 rounded-lg bg-white/[0.04] border border-white/[0.06] shrink-0 flex items-center justify-center text-[#2a3547] text-[10px]">No img</div>
                }
                <div className="flex-1 min-w-0 max-[640px]:w-full">
                  <p className="text-white font-semibold text-sm truncate">{b.title || "(no title)"}</p>
                  <p className="text-[#4a5568] text-xs truncate">{b.ctaText} &rarr; {b.ctaLink}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0 max-[640px]:w-full max-[640px]:justify-end">
                  <button onClick={() => toggleBanner(i)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-all cursor-pointer ${
                      b.active ? "text-green-400 border-green-400/30 bg-green-400/10" : "text-[#4a5568] border-white/[0.1] bg-white/[0.03]"
                    }`}>
                    {b.active ? "Active" : "Off"}
                  </button>
                  <button onClick={() => setEditBanner({ index: i, data: { ...b } })}
                    className="text-xs px-3 py-1 rounded-lg border border-[rgba(0,212,255,0.3)] text-[#00d4ff] hover:bg-[rgba(0,212,255,0.1)] cursor-pointer transition-all">Edit</button>
                  <button onClick={() => deleteBanner(i)}
                    className="text-xs px-3 py-1 rounded-lg border border-red-500/20 text-red-400 hover:bg-red-500/10 cursor-pointer transition-all">Delete</button>
                </div>
              </div>
            ))}
          </div>

          {editBanner && (
              <div className="p-5 bg-white/[0.03] border border-[rgba(0,212,255,0.2)] rounded-2xl max-[640px]:p-4">
              <h3 className="text-white font-bold mb-4">{editBanner.index === -1 ? "Add Banner" : `Edit Banner #${editBanner.index + 1}`}</h3>
              <BannerForm
                data={editBanner.data}
                onChange={(field, val) => setEditBanner({ ...editBanner, data: { ...editBanner.data, [field]: val } })}
                onUpload={(file) => handleImageUpload("banner", file)}
                uploading={assetUploading === "banner"}
              />
              <div className="flex gap-2 mt-4">
                <button onClick={saveBanner} disabled={saving === "adBanners"}
                  className="px-4 py-2 bg-[rgba(0,212,255,0.15)] text-[#00d4ff] border border-[rgba(0,212,255,0.3)] rounded-lg text-sm hover:bg-[rgba(0,212,255,0.25)] cursor-pointer disabled:opacity-50">
                  {saving === "adBanners" ? "Saving..." : "Save Banner"}
                </button>
                <button onClick={() => setEditBanner(null)}
                  className="px-4 py-2 text-[#4a5568] border border-white/[0.08] rounded-lg text-sm hover:border-white/[0.2] cursor-pointer">Cancel</button>
              </div>
              {saved === "adBanners" && <p className="text-green-400 text-xs mt-2">\u2713 Saved!</p>}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════ FEATURE BAR ═══════════════════ */}
      {tab === "features" && (
        <div>
          <p className="text-[#a0aec0] text-sm mb-4">Edit the 4 trust/feature bar items shown below the hero</p>
          <div className="space-y-3 mb-4">
            {featureBar.map((f, i) => (
              <div key={i} className="p-4 bg-white/[0.03] border border-white/[0.07] rounded-xl grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                <div>
                  <label className="text-xs text-[#4a5568] mb-1 block">Title</label>
                  <input className={inputCls} value={f.title} onChange={e => updateFeature(i, "title", e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-[#4a5568] mb-1 block">Description</label>
                  <input className={inputCls} value={f.desc} onChange={e => updateFeature(i, "desc", e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-[#4a5568] mb-1.5 block">Accent Color</label>
                  <AccentPicker value={f.accent || "#00d4ff"} onChange={v => updateFeature(i, "accent", v)} />
                </div>
              </div>
            ))}
          </div>
          <SaveBtn saving={saving} saved={saved} section="featureBar" label="Save Feature Bar"
            onClick={() => saveSection("featureBar", featureBar)} />
        </div>
      )}

      {/* ═══════════════════ BRANDS ═══════════════════ */}
      {tab === "brands" && (
        <div>
          <p className="text-[#a0aec0] text-sm mb-4">{brands.length} brands in the marquee strip</p>
          <div className="flex flex-wrap gap-2 mb-6">
            {brands.map((b, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm">
                <span className="text-lg">{b.icon}</span>
                <span className="text-[#a0aec0] font-medium">{b.name}</span>
                <button onClick={() => saveSection("brands", brands.filter((_, idx) => idx !== i))}
                  className="text-[#4a5568] hover:text-red-400 transition-colors cursor-pointer ml-1 text-base leading-none">&times;</button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 items-end p-4 bg-white/[0.02] border border-white/[0.06] rounded-xl">
            <div>
              <label className="text-xs text-[#4a5568] mb-1 block">Brand Name</label>
              <input className={inputCls} style={{ width: 150 }} placeholder="e.g. Sony"
                value={newBrand.name} onChange={e => setNewBrand({ ...newBrand, name: e.target.value })}
                onKeyDown={e => e.key === "Enter" && newBrand.name.trim() && saveSection("brands", [...brands, newBrand]) && setNewBrand({ name: "", icon: "" })} />
            </div>
            <div>
              <label className="text-xs text-[#4a5568] mb-1 block">Icon (emoji)</label>
              <input className={inputCls} style={{ width: 70 }} placeholder="\uD83C\uDFA7"
                value={newBrand.icon} onChange={e => setNewBrand({ ...newBrand, icon: e.target.value })} />
            </div>
            <button
              onClick={() => { if (!newBrand.name.trim()) return; saveSection("brands", [...brands, newBrand]); setNewBrand({ name: "", icon: "" }); }}
              className="px-4 py-2 bg-[rgba(0,212,255,0.15)] text-[#00d4ff] border border-[rgba(0,212,255,0.3)] rounded-lg text-sm hover:bg-[rgba(0,212,255,0.25)] cursor-pointer transition-colors">
              + Add
            </button>
          </div>
          {saving === "brands" && <p className="text-[#4a5568] text-xs mt-2">Saving...</p>}
          {saved === "brands" && <p className="text-green-400 text-xs mt-2">\u2713 Saved!</p>}
        </div>
      )}

      {/* ═══════════════════ TICKER ═══════════════════ */}
      {tab === "ticker" && (
        <div>
          <p className="text-[#a0aec0] text-sm mb-4">Announcement messages scrolling in the top ticker bar</p>
          <div className="space-y-2 mb-5">
            {tickers.map((t, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-white/[0.03] border border-white/[0.07] rounded-xl">
                <span className="flex-1 text-[#a0aec0] text-sm">{t}</span>
                <button onClick={() => saveSection("tickers", tickers.filter((_, idx) => idx !== i))}
                  className="text-xs px-2.5 py-1 rounded-lg border border-red-400/20 text-red-400 hover:bg-red-400/10 cursor-pointer transition-all shrink-0">Delete</button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input className={`${inputCls} flex-1`} placeholder="e.g. \u26A1 Flash Sale \u2013 40% OFF on Laptops"
              value={newTicker} onChange={e => setNewTicker(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && newTicker.trim()) { saveSection("tickers", [...tickers, newTicker]); setNewTicker(""); } }} />
            <button onClick={() => { if (!newTicker.trim()) return; saveSection("tickers", [...tickers, newTicker]); setNewTicker(""); }}
              className="px-4 py-2 bg-[rgba(0,212,255,0.15)] text-[#00d4ff] border border-[rgba(0,212,255,0.3)] rounded-lg text-sm hover:bg-[rgba(0,212,255,0.25)] cursor-pointer transition-colors shrink-0">
              + Add
            </button>
          </div>
          {saving === "tickers" && <p className="text-[#4a5568] text-xs mt-2">Saving...</p>}
          {saved === "tickers" && <p className="text-green-400 text-xs mt-2">\u2713 Saved!</p>}
        </div>
      )}
    </div>
  );
};

export default AdminHomePage;
